import { z } from "zod";
import {
  cancellationReason,
  identityMethodId,
  paymentErrorCategory,
  paymentStatus,
} from "./ftthOrchestrator";

export const state = z.enum([
  "START",
  "IDENTITY",
  "IDENTITY_PENDING",
  "IDENTITY_ERROR",
  "PAYMENT_METHOD",
  "APPOINTMENT",
  "CONSENTS",
  "ORDER_SUMMARY",
  "ORDER_COMMITTED",
  "ORDER_PAYMENT",
  "ORDER_ACTIVATE",
  "ORDER_APPOINTMENT_ERROR",
  "ORDER_PAYMENT_ERROR",
  "ORDER_FULFILLMENT",
  "ORDER_FULFILLMENT_FAILED",
  "ORDER_FINISHING",
  "ORDER_HELD",
  "ORDER_PENDING_CANCELLATION",
  "ORDER_FULFILLMENT_FAILED",
  "COMPLETED",
  "RETURN_PENDING",
  "NOT_RETURNED_PENALTY",
  "ACTIVATION",
  "MNP",
  "LEGACY_FEASIBILITY_PENDING",
  "LEGACY_MIGRATION",
  "LEGACY_MIGRATION_ERROR",
  "LEGACY_MIGRATION_PENDING",
  "DELIVERY",
  "SIM",
  "CONTRACT_VERIFICATION",
  "DOCUMENTS_SENDING",
  "AGREEMENT_DATA",
  "AGREEMENT",
]);

const actionsRel = z.enum([
  "ORDER_COMMIT",
  "CONSENTS_SET",
  "DELIVERY_METHOD_SET",
  "DELIVERY_METHOD_UPDATE",
  "CONTRACT_VERIFICATION_SETUP",
  "CONTRACT_VERIFICATION_CHECK",
  "TECHNICAL_CONTRACT_VERIFICATION_SETUP",
  "TECHNICAL_CONTRACT_VERIFICATION_CHECK",
  "ORDER_CANCEL",
  "ORDER_CREATE",
  "ORDER_PAYMENT_REPEAT",
  "APPOINTMENT_SLOT_CHANGE",
  "TECHNICAL_IDENTITY_CHECK",
  "IDENTITY_CHECK",
  "PAYMENT_METHOD_SET",
  "IDENTITY_START",
  "IDENTITY_SETUP",
  "APPOINTMENT_SLOT_RESERVE",
  "APPOINTMENT_SLOT_CHANGE_FULFILLMENT",
  "REACTIVATION_ORDER_COMMIT",
  "MIGRATION_CONFIRM",
  "SIM_SET",
  "ORDER_ACTIVATE",
  "MNP_MODE_SET",
  "MEMBER_ONBOARDING_CREATE",
  "MULTI_ORDER_SETUP",
  "ITEM_DELETE",
  "TRY_AGAIN",
  "AGREEMENT_DATA_SET",
  "AGREEMENT_SIGN",
  "AGREEMENT_DOWNLOAD",
]);

export const actions = z.array(
  z.object({
    href: z.string(),
    method: z.string(),
    rel: actionsRel,
  }),
);
const shippingStatus = z.enum([
  "PACK_CHAN",
  "DELV_PROGR_CHAN",
  "DELV_CHAN",
  "RET_PROGR_CHAN",
  "RET_CHAN",
  "AGGR_CHAN2",
  "AGGR_CHAN3",
  "ACC_CHAN",
  "CANC_CHAN",
]);

const mnp = z
  .object({
    donor: z.string().optional(),
    transferDate: z.string().optional(),
    npMessageCode: z.string().optional(),
    reasonCode: z.string().optional(),
    state: z.string().optional(),
  })
  .optional();

const delivery = z.object({
  country: z.string().optional(),
  street: z.string().optional(),
  streetNumber: z.string(),
  apartmentNumber: z.string().optional(),
  municipality: z.string(),
  postCode: z.string(),
  stateOrProvince: z.string().optional(),
  community: z.string().optional(),
  district: z.string().optional(),
  note: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  CBSReference: z
    .object({
      communityId: z.string().optional(),
      districtId: z.string().optional(),
      municipalityId: z.string().optional(),
      stateOrProvinceId: z.string().optional(),
      streetId: z.string().optional(),
      countryId: z.number().optional(),
    })
    .optional(),
});

const orderSummary = z.object({
  totalOrderPrice: z.number(),
  totalRecurringOrderPrice: z.number(),
  totalOneTimeOrderPrice: z.number(),
  discount: z.number().optional(),
  totalOrderDiscount: z.number().optional(),
  totalRecurringOrderBasePrice: z.number().optional(),
  items: z.array(
    z.object({
      offerId: z.string(),
      offerPrice: z.number(),
      offerPriceType: z.string(),
    }),
  ),
});

const deliveryMethodId = z.enum([
  "COURIER",
  "ESIM",
  "BLISTER",
  "TECHNICAL_PARTNER_DELIVERY",
  "PREOWNED",
]);

const offerCategory = z
  .union([z.literal("MOBILE"), z.literal("MOBILE_POSTPAID")])
  .optional();

export const mobileOrchestrator = z.object({
  type: z.literal("NJU_MOBILE_ONBOARDING"),
  contactNumber: z.string().optional(),
  paymentStatus: paymentStatus.optional(),
  cancellationReason: cancellationReason.optional(),
  id: z.string(),
  adminId: z.string().optional(),
  offerIds: z.array(z.string()),
  offerCategory,
  applicantId: z.string().optional(),
  onfidoVerificationExceeded: z.boolean().optional(),
  paymentTimestamp: z.string().optional(),
  orderId: z.string().optional(),
  userId: z.string(),
  verificationStatusReason: z.string().optional(),
  njuCustomerMsisdn: z.string().optional(),
  delivery: delivery.optional(),
  deliveryMethodId: deliveryMethodId.optional(),
  hasConsentsSet: z.boolean().optional(),
  isUserVerified: z.boolean().optional(),
  orderSummary: orderSummary.optional(),
  orderStatus: z.string().optional(),
  verifiedMsisdn: z.string().optional(),
  simCode: z.string().optional(),
  actions,
  paymentErrorCategory: paymentErrorCategory.optional(),
  identityMethodId: identityMethodId.optional(),
  state,
  shippingStatus: shippingStatus.optional(),
  waybillNumber: z.string().optional(),
  primaryPaymentMethodId: z.string().optional(),
  userVerificationStatus: z.string().optional(),
  hasIdentityStarted: z.boolean().optional(),
  identitySetupLock: z.boolean().optional(),
  processId: z.string().optional(),
  legacy: z
    .object({
      feasibilityCode: z
        .enum([
          // code descriptions: https://dop-orange.atlassian.net/browse/DOP-28419
          "RC001",
          "RC002",
          "RC003",
          "RC004",
          "RC005",
          "RC006",
          "RC007",
          "RC008",
          "RC009",
          "RC011",
          "RC012",
          "RC013",
          "RC014",
        ])
        .optional(),
      feasibilityReason: z.string().optional(),
      isMigrationFeasible: z.boolean().optional(),
      isK2Migration: z.boolean().optional(),
      blockingGroups: z.string().optional(),
    })
    .optional(),
  mnp,
  orderSimNumber: z.string().optional(),
});

export const groupOrchestrator = z.object({
  type: z.literal("NJU_GROUP_ONBOARDING"),
  contactNumber: z.string().optional(),
  paymentStatus: paymentStatus.optional(),
  cancellationReason: cancellationReason.optional(),
  id: z.string(),
  offerIds: z.array(z.string()),
  offerCategory: z.string().optional(),
  applicantId: z.string().optional(),
  onfidoVerificationExceeded: z.boolean().optional(),
  paymentTimestamp: z.string().optional(),
  orderId: z.string().optional(),
  userId: z.string(),
  verificationStatusReason: z.string().optional(),
  njuCustomerMsisdn: z.string().optional(),
  delivery: delivery.optional(),
  isUserVerified: z.boolean().optional(),
  orderSummary: orderSummary.optional(),
  orderStatus: z.string().optional(),
  verifiedMsisdn: z.string().optional(),
  simCode: z.string().optional(),
  payload: z
    .object({ id: z.string(), type: z.string(), userId: z.string() })
    .optional(),
  paymentErrorCategory: paymentErrorCategory.optional(),
  identityMethodId: identityMethodId.optional(),
  state,
  shippingStatus: shippingStatus.optional(),
  waybillNumber: z.string().optional(),
  primaryPaymentMethodId: z.string().optional(),
  userVerificationStatus: z.string().optional(),
  hasIdentityStarted: z.boolean().optional(),
  identitySetupLock: z.boolean().optional(),
  processId: z.string().optional(),
  orderSimNumber: z.string().optional(),
});

type IMobileOrchestratorApi = z.infer<typeof mobileOrchestrator>;

export type IMobileOrchestrator = IMobileOrchestratorApi & {
  offerId?: string;
  contactNumberWithoutCountyCode?: string;
  isFreeFromActivationFee?: boolean;
};
